import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { IntlProvider, useIntl, FormattedMessage } from 'react-intl';

import {getStartParam, clearStartParam, isTelegramWebApp} from './services/telegramService';

// Импорты ваших провайдеров
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth, SUPPORTED_LANGUAGES, DEFAULT_LOCALE } from './contexts/AuthContext';
import { NotificationProvider } from "./contexts/NotificationContext.jsx";
import { PageLoaderProvider } from "./contexts/PageLoaderContext";

// Импорты ваших страниц и компонентов
import BottomNav from './components/BottomNav/BottomNav';
import HomePage from './pages/HomePage/HomePage';
// ... и все остальные ваши страницы ...
import SettingsPage from './pages/SettingsPage/SettingsPage';
import CreateFormPage from './pages/CreateFormPage/CreateFormPage';
import Preloader from './components/common/Preloader/Preloader';
import TelegramInitializer from "./components/TelegramInitializer.js";
import NotificationContainer from "./components/common/NotificationContainer/NotificationContainer";
import PageLoaderIndicator from "./components/common/PageLoaderIndicator/PageLoaderIndicator";
import FullScreenMessage from "./components/common/FullScreenMessage/FullScreenMessage";

// Импорты переводов
import messages_en from './translations/en.json';
import messages_ru from './translations/ru.json';
import TemplateSelectionPage from "./pages/TemplateSelectionPage/TemplateSelectionPage";
import FormSettingsPage from "./pages/FormSettingsPage/FormSettingsPage";
import EditFormPage from "./pages/EditFormPage/EditFormPage";
import AnswersListPage from "./pages/AnswersListPage/AnswersListPage";
import SingleAnswerPage from "./pages/SingleAnswerPage/SingleAnswerPage";
import StatisticsPage from "./pages/StatisticsPage/StatisticsPage";
import LanguageSelectionPage from "./pages/LanguageSelectionPage/LanguageSelectionPage";
import SuggestIdeaPage from "./pages/SuggestIdeaPage/SuggestIdeaPage";
import PublicFormPage from "./pages/PublicFormPage/PublicFormPage";
import DocumentPage from "./pages/DocumentPage/DocumentPage";
import FaqPage from "./pages/FaqPage/FaqPage";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";

const messages = {
    en: messages_en,
    ru: messages_ru,
};

// --- КОМПОНЕНТ, КОТОРЫЙ РЕНДЕРИТ МАРШРУТЫ И ИСПОЛЬЗУЕТ ВСЕ ХУКИ ---
const AppRoutes = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl(); // Теперь useIntl() вызывается внутри IntlProvider

    // Логика Deep-link
    useEffect(() => {
        // Этот эффект запускается один раз при загрузке приложения
        const startParam = getStartParam();

        if (startParam) {
            const [command, ...payloadParts] = startParam.split('-');
            const payload = payloadParts.join('-');
            if (command === 'view_form' && payload) {
                // Здесь payload - это slug, например "my-cool-form"
                navigate(`/form/${payload}`);
            }
            else if (command === 'view_submission' && payload) {
                // Теперь payload - это "2-70"
                // Мы можем безопасно разделить его снова, зная, что в нем только один дефис
                const [formId, submissionId] = payload.split('-');

                if (formId && submissionId) {
                    navigate(`/form/${formId}/answers/${submissionId}`);
                }
            }
            clearStartParam();
        }
    }, [navigate]);

    // Проверка на окружение Telegram
    const { user, isUserLoaded } = useAuth();
    const isTelegramEnv = isTelegramWebApp();
    if (!isTelegramEnv && !user?.telegram_id) {
        const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
        const botLink = botUsername ? `https://t.me/${botUsername}` : '#';
        return (
            <FullScreenMessage
                emoji="🤖"
                title={intl.formatMessage({ id: "auth.required.title" })}
                text={intl.formatMessage({ id: "auth.required.text" })}
                buttonProps={{
                    text: intl.formatMessage({ id: "auth.required.button" }),
                    onClick: () => window.location.href = botLink,
                }}
            />
        );
    }

    const navVisiblePaths = ['/', '/create', '/settings'];
    const isNavVisible = navVisiblePaths.includes(location.pathname);

    return (
        <>
            <ScrollToTop/>
            <TelegramInitializer/>
            <NotificationContainer />
            <PageLoaderIndicator />
            <div className={'container ' + (isNavVisible ? 'container--bottom' : '')}>
                <Routes>
                    <Route path="/" element={<HomePage/>}/>
                    <Route path="/create-form" element={<CreateFormPage />} />
                    <Route path="/create" element={<TemplateSelectionPage />}/>
                    <Route path="/settings" element={<SettingsPage/>}/>

                    <Route path="/form/:formId/settings" element={<FormSettingsPage />} />
                    <Route path="/form/:formId/edit" element={<EditFormPage />} />
                    <Route path="/form/:formId/answers" element={<AnswersListPage />} />
                    <Route path="/form/:formId/answers/:submissionId" element={<SingleAnswerPage />} />
                    <Route path="/form/:formId/statistics" element={<StatisticsPage />} />

                    <Route path="/settings/language" element={<LanguageSelectionPage />} /> {/* <--- Новый маршрут */}
                    <Route path="/settings/suggest-idea" element={<SuggestIdeaPage />} />

                    <Route path="/form/:slug" element={<PublicFormPage />} />

                    <Route path="/privacy-policy" element={<DocumentPage documentSlug="privacy-policy" />} />
                    <Route path="/terms-of-use" element={<DocumentPage documentSlug="terms-of-use" />} />
                    <Route path="/faq" element={<FaqPage />} />

                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </div>
            {isNavVisible && <BottomNav />}
        </>
    );
};

// --- КОМПОНЕНТ, КОТОРЫЙ ОПРЕДЕЛЯЕТ ЯЗЫК И СОЗДАЕТ IntlProvider ---
const AppContent = () => {
    const { user, isUserLoaded } = useAuth();

    // Логика определения языка остается здесь
    const locale = (user && SUPPORTED_LANGUAGES.includes(user.lang)) ? user.lang : DEFAULT_LOCALE;

    return (
        <IntlProvider locale={locale} messages={messages[locale]} onError={() => {}}>
            {/* Теперь IntlProvider оборачивает всю логику.
                Мы проверяем isUserLoaded ВНУТРИ него.
            */}
            {!isUserLoaded ? (
                // Передаем в Preloader уже локализованное сообщение
                <Preloader message={<FormattedMessage id="app.loading" />} />
            ) : (
                // AppRoutes будет отрендерен, когда пользователь загружен
                <AppRoutes />
            )}
        </IntlProvider>
    );
};

// Вспомогательный компонент для скролла
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

// --- ГЛАВНЫЙ КОМПОНЕНТ APP, КОТОРЫЙ СОДЕРЖИТ ВСЕ ПРОВАЙДЕРЫ ---
function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <NotificationProvider>
                    <PageLoaderProvider>
                        <Router>
                            {/* AppContent будет иметь доступ к AuthProvider, но не к Router */}
                            <AppContent />
                        </Router>
                    </PageLoaderProvider>
                </NotificationProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;
